/**
 * MathLive configuration and utilities
 */

import type { MathLiveConfig, VirtualKeyboardConfig, VirtualKeyboardCustomLayout } from '../types';

export const DEFAULT_MATHLIVE_CONFIG: MathLiveConfig = {
  virtualKeyboardMode: 'manual',
  smartFence: true,
  smartSuperscript: true,
  scriptDepth: 2,
  removeExtraneousParentheses: true,
};

// Custom SPINE math keyboard layout
// Atoms: integers, decimals, +, -, ×, ÷
// Molecules: exponents, roots, scientific notation, fractions (5th column)
// Actions: backspace, clear (6th column)
export const SPINE_MATH_LAYOUT: VirtualKeyboardCustomLayout = {
  label: 'Math',
  rows: [
    [{ latex: '7' }, { latex: '8' }, { latex: '9' }, { latex: '\\div' }, { latex: '\\frac{#@}{#?}', label: 'a/b' }, { command: 'deleteBackward', label: '⌫' }],
    [{ latex: '4' }, { latex: '5' }, { latex: '6' }, { latex: '\\times' }, { latex: '#@^{#?}', label: 'xⁿ' }, { command: 'deleteAll', label: 'Clear' }],
    [{ latex: '1' }, { latex: '2' }, { latex: '3' }, { latex: '-' }, { latex: '\\sqrt{#@}', label: '√' }, { latex: '', label: ' ' }],
    [{ latex: '0' }, { latex: '.' }, { latex: '=' }, { latex: '+' }, { latex: '\\times10^{#?}', label: '×10ⁿ' }, { latex: '', label: ' ' }],
  ],
};

// Default virtual keyboard config
export const DEFAULT_KEYBOARD_CONFIG: VirtualKeyboardConfig = {
  layouts: [SPINE_MATH_LAYOUT],
};

export const getMathLiveOptions = (config: Partial<MathLiveConfig> = {}): Record<string, any> => {
  const finalConfig = { ...DEFAULT_MATHLIVE_CONFIG, ...config };

  return {
    smartFence: finalConfig.smartFence,
    smartSuperscript: finalConfig.smartSuperscript,
    removeExtraneousParentheses: finalConfig.removeExtraneousParentheses,
    readOnly: false,
  };
};

/**
 * Common LaTeX templates for quick insertion
 */
export const COMMON_TEMPLATES = {
  fraction: '\\frac{#0}{#1}',
  squareRoot: '\\sqrt{#0}',
  nthRoot: '\\sqrt[#0]{#1}',
  exponent: '#0^{#1}',
  subscript: '#0_{#1}',
  sum: '\\sum_{#0}^{#1}',
  integral: '\\int_{#0}^{#1}',
  limit: '\\lim_{#0 \\to #1}',
  derivative: '\\frac{d#0}{d#1}',
  partialDerivative: '\\frac{\\partial #0}{\\partial #1}',
  matrix2x2: '\\begin{pmatrix} #0 & #1 \\\\ #2 & #3 \\end{pmatrix}',
  equation: '#0 = #1',
  inequality: '#0 \\leq #1',
  absolute: '\\left|#0\\right|',
  parentheses: '\\left(#0\\right)',
  brackets: '\\left[#0\\right]',
  braces: '\\left\\{#0\\right\\}',
};

/**
 * Get template categories for UI organization
 */
export const TEMPLATE_CATEGORIES = {
  basic: {
    label: 'Basic',
    templates: ['fraction', 'squareRoot', 'exponent', 'subscript'],
  },
  calculus: {
    label: 'Calculus',
    templates: ['derivative', 'partialDerivative', 'integral', 'limit', 'sum'],
  },
  algebra: {
    label: 'Algebra',
    templates: ['equation', 'inequality', 'nthRoot', 'absolute'],
  },
  brackets: {
    label: 'Brackets',
    templates: ['parentheses', 'brackets', 'braces'],
  },
  matrices: {
    label: 'Matrices',
    templates: ['matrix2x2'],
  },
} as const;